import React from 'react';
import { Box, Grid, Skeleton, Stack, TableCell, TableRow } from '@mui/material';

export function StatCardGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} variant="rounded" height={108} sx={{ borderRadius: 2 }} />
      ))}
    </>
  );
}

export function CardGridSkeleton({
  count = 6,
  height = 200,
  columns = { xs: 12, sm: 6, lg: 4 },
}: {
  count?: number;
  height?: number;
  columns?: { xs?: number; sm?: number; lg?: number };
}) {
  return (
    <Grid container spacing={1.5}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item key={index} xs={columns.xs ?? 12} sm={columns.sm ?? 6} lg={columns.lg ?? 4}>
          <Skeleton variant="rounded" height={height} sx={{ borderRadius: 2 }} />
        </Grid>
      ))}
    </Grid>
  );
}

export function ChipRowSkeleton({ count = 4 }: { count?: number }) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} variant="rounded" width={index === 0 ? 68 : 84} height={24} sx={{ borderRadius: 999 }} />
      ))}
    </Stack>
  );
}

export function TableRowsSkeleton({
  rows = 6,
  columns = 5,
  avatarColumn = false,
}: {
  rows?: number;
  columns?: number;
  avatarColumn?: boolean;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <TableCell key={colIndex}>
              {avatarColumn && colIndex === 0 ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton variant="text" width="60%" />
                </Stack>
              ) : (
                <Skeleton variant="text" width={colIndex === 0 ? '72%' : '48%'} />
              )}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export function SectionPanelSkeleton({ height = 140 }: { height?: number }) {
  return <Skeleton variant="rounded" height={height} sx={{ borderRadius: 2 }} />;
}

export function CircuitDetailSkeleton() {
  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Skeleton variant="text" width="42%" height={36} />
        <Skeleton variant="text" width="58%" height={22} />
      </Stack>
      <Skeleton variant="rounded" height={360} sx={{ borderRadius: 2 }} />
      <ChipRowSkeleton count={4} />
      <SectionPanelSkeleton height={120} />
    </Stack>
  );
}

export function PlanCurrentPanelSkeleton() {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1}>
        <Skeleton variant="rounded" width={96} height={24} sx={{ borderRadius: 999 }} />
        <Skeleton variant="text" width="48%" />
      </Stack>
      <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
    </Stack>
  );
}

export function DriverProfileSkeleton() {
  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center' }}>
        <Skeleton variant="circular" width={64} height={64} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="text" width="55%" />
        </Box>
      </Stack>
      <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2 }} />
    </Stack>
  );
}

export function HeaderStatBoxesSkeleton({ count = 3 }: { count?: number }) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} variant="rounded" width={126} height={52} sx={{ borderRadius: 1 }} />
      ))}
    </Stack>
  );
}
